import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Image } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppSelect } from '../../constants/appSelect';
import { newObjectKeys, formatDateVN, DEFAULT_THUMB, dataToOptionSelect } from '../../utils/utility';
import { deleteLesson, getLessons } from '../../services/lesson';
import { getCurriculums } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
import isEqual from 'lodash/isEqual';

const itemName = 'Bài học';
const createItemLink = '/cms/quan-ly-bai-hoc/them-bai-hoc';
const editItemLink = '/cms/quan-ly-bai-hoc/sua-bai-hoc';
const { Text } = Typography;

const Lesson = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  // load curriculums for filter
  const { data: curriculumListRes } = useQuery({
    queryKey: ['curriculumFilterList'],
    queryFn: () => getCurriculums({ page: 1, limit: 1000, status: [1] }),
  });
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategoriesForLessonFilter'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const curriculumOptions = useMemo(() => {
    const curr = Array.isArray(curriculumListRes?.data) ? curriculumListRes.data : [];
    const cats = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id;
      const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) {
        guard.add(cur);
        parts.unshift(titleMap[cur] || '');
        cur = parentMap[cur] || 0;
      }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const opts = curr.map((c) => {
      const catPath = toPath(c.category_id);
      return {
        value: c.id,
        label: `${catPath || '—'} - ${c.title || ''}`.trim(),
        _cat: (catPath || '').toLowerCase(),
        _title: (c.title || '').toLowerCase(),
      };
    });
    opts.sort((a, b) => a._cat.localeCompare(b._cat) || a._title.localeCompare(b._title));
    return [{ value: -1, label: 'Tất cả' }, ...opts];
  }, [curriculumListRes, allCategoriesRes]);
  const curriculumMap = useMemo(() => {
    const arr = curriculumListRes?.data || [];
    const map = new Map();
    arr.forEach((i) => map.set(i.id, i.title));
    return map;
  }, [curriculumListRes]);
  // Build map: curriculum id -> "Parent — Child — … - Program title"
  const curriculumPathLabelMap = useMemo(() => {
    const curr = Array.isArray(curriculumListRes?.data) ? curriculumListRes.data : [];
    const cats = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id;
      const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) {
        guard.add(cur);
        parts.unshift(titleMap[cur] || '');
        cur = parentMap[cur] || 0;
      }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const map = {};
    curr.forEach((c) => {
      const catPath = toPath(c.category_id);
      map[c.id] = `${catPath || '—'} - ${c.title || ''}`.trim();
    });
    return map;
  }, [curriculumListRes, allCategoriesRes]);

  // Initialize params from URL on first load
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const keyword = searchParams.get('keyword') || undefined;
    const statusStr = searchParams.get('status');
    const statusVal = statusStr !== null ? Number(statusStr) : -1;
    const curriculumStr = searchParams.get('curriculum_id');
    const curriculumVal = curriculumStr !== null ? Number(curriculumStr) : -1;

    const next = { page, limit };
    if (keyword) Object.assign(next, { keyword });
    if (statusVal >= 0) Object.assign(next, { status: [statusVal] });
    if (curriculumVal >= 0) Object.assign(next, { curriculum_id: curriculumVal });

    setParams(next);
    formSearch.setFieldsValue({
      keyword: keyword,
      status: statusVal >= 0 ? statusVal : -1,
      curriculum_id: curriculumVal >= 0 ? curriculumVal : -1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push params to URL when they change
  useEffect(() => {
    if (!params) return;
    const sp = {
      page: String(params.page || 1),
      limit: String(params.limit || 20),
    };
    if (params.keyword) sp.keyword = params.keyword;
    if (Array.isArray(params.status) && params.status.length > 0 && params.status[0] >= 0) {
      sp.status = String(params.status[0]);
    }
    if (params.curriculum_id >= 0) {
      sp.curriculum_id = String(params.curriculum_id);
    }
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  const {
    data: dataLessons,
    isPending,
    refetch: refetchDataLessons,
  } = useQuery({ queryKey: ['dataLessons', params], queryFn: () => getLessons(params) });

  const { mutate: mutateDeleteLesson } = useMutation({
    mutationFn: deleteLesson,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Xóa ${itemName} thành công!` });
        refetchDataLessons();
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.keyword) Object.assign(temp, { keyword: values?.keyword });
    if (values?.status >= 0) Object.assign(temp, { status: [values?.status] });
    if (values?.curriculum_id >= 0) Object.assign(temp, { curriculum_id: values?.curriculum_id });

    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetchDataLessons();
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'id',
      key: 'stt',
      width: 70,
      fixed: 'left',
      align: 'center',
      ellipsis: true,
      onHeaderCell: () => ({ style: { maxWidth: 70 } }),
      onCell: () => ({ style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }),
      render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1,
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (_, { title, id }) => (
        <Link to={`${editItemLink}/${id}`} className="text-[#1677FF]">
          {title}
        </Link>
      ),
      minWidth: 150,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Chương trình',
      dataIndex: 'curriculum_id',
      key: 'curriculum_id',
      render: (_, rec) => <Text>{curriculumPathLabelMap[rec?.curriculum_id] || '-'}</Text>,
      minWidth: 220,
      width: 320,
    },
    {
      title: 'Video',
      key: 'videos',
      align: 'center',
      width: 150,
      render: (_, rec) => {
        const lessonId = rec?.id;
        const count = rec?.video_count ?? rec?.videos_count ?? rec?.video_total;
        const query = `?page=1&limit=20&lesson_id=${lessonId}`;
        const listLink = `/cms/quan-ly-video${query}`;
        const createLink = `/cms/quan-ly-video/them-video?lesson_id=${lessonId}`;
        // If count is known and 0 -> show create; if >0 -> show list; if unknown -> show list
        if (typeof count === 'number') {
          if (count > 0) {
            return (
              <Link to={listLink} className="text-[#1677FF]" title="Xem danh sách video của bài học">
                Xem video ({count})
              </Link>
            );
          }
          return (
            <Link to={createLink}>
              <Button type="link" icon={<PlusOutlined />}>Tạo video</Button>
            </Link>
          );
        }
        return (
          <Link to={listLink} className="text-[#1677FF]" title="Xem danh sách video của bài học">
            Xem video
          </Link>
        );
      },
    },
    {
      title: 'Đề ôn tập',
      key: 'quizzes_link',
      align: 'center',
      width: 160,
      render: (_, rec) => {
        const lessonId = rec?.id;
        const count = rec?.quiz_count ?? rec?.quizzes_count ?? rec?.quiz_total;
        const query = `?page=1&limit=20&lesson_id=${lessonId}`;
        const listLink = `/cms/quan-ly-de-on-tap${query}`;
        const createLink = `/cms/quan-ly-de-on-tap/them-de-on-tap?lesson_id=${lessonId}`;
        if (typeof count === 'number') {
          if (count > 0) {
            return (
              <Link to={listLink} className="text-[#1677FF]" title="Xem danh sách đề ôn tập của bài học">
                Xem đề ôn tập ({count})
              </Link>
            );
          }
          return (
            <Link to={createLink}>
              <Button type="link" icon={<PlusOutlined />}>Tạo đề ôn tập</Button>
            </Link>
          );
        }
        return (
          <Link to={listLink} className="text-[#1677FF]" title="Xem danh sách đề ôn tập của bài học">
            Xem đề ôn tập
          </Link>
        );
      },
    },
    {
      title: 'Thứ tự',
      dataIndex: 'order',
      key: 'order',
      width: 90,
      align: 'center',
    },
    {
      title: 'Học thử',
      dataIndex: 'free_trial',
      key: 'free_trial',
      width: 100,
      align: 'center',
      render: (free_trial) => (free_trial === 1 ? 'Có' : 'Không'),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (_, { updated_at }) => <Space direction="vertical">{updated_at && <Text className="text-[#667085]">{formatDateVN(updated_at)}</Text>}</Space>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={(AppSelect.STATUS || []).find((item) => item?.value === status)?.color}>
          {(AppSelect.STATUS || []).find((item) => item?.value === status)?.label}
        </Tag>
      ),
      width: 70,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`${editItemLink}/${record?.id}`)}>
            <EditOutlined />
          </Button>
          {record?.status !== 2 && (
            <Button type="text" onClick={() => mutateDeleteLesson(record?.id)}>
              <DeleteOutlined />
            </Button>
          )}
        </Space>
      ),
      width: 70,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách {itemName}</h1>
        <Link to={createItemLink}>
          <Button type="primary">
            <PlusOutlined />
            Thêm {itemName}
          </Button>
        </Link>
      </div>

      <Form name="search_lessons" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="keyword" label={<Text>Từ khóa</Text>}>
              <Input placeholder="Nhập từ khóa tìm kiếm..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...(AppSelect.STATUS || [])]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={18} md={12} xl={9}>
            <Form.Item name="curriculum_id" label={<Text>Chương trình</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={curriculumOptions}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params &&
              Object.values(newObjectKeys(params, ['keyword', 'status', 'curriculum_id'])).some((value) => value) && (
                <Button
                  htmlType="button"
                  onClick={() => {
                    formSearch.resetFields();
                    setParams({ page: 1, limit: params.limit });
                  }}
                >
                  <DeleteOutlined />
                  Xóa tìm kiếm
                </Button>
              )}
          </Col>
        </Row>
      </Form>

      <TableCustom
        columns={columns}
        dataSource={dataLessons?.data ?? []}
        totalItem={dataLessons?.total ?? 0}
        loading={isPending}
        params={params}
        setParams={setParams}
      />
    </div>
  );
};

export default Lesson;
